/**
 * Referral-related data fetchers
 */
import { QueryClient, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { createOrUpdateReferral } from "@/src/actions/referral";
import { toast } from "@/src/hooks/use-toast";
import { updateAppointmentsCache } from "@/src/lib/data-fetchers/appointments-fetchers";
import { TASKS_CACHE_KEY, TASK_CACHE_KEY } from "@/src/lib/data-fetchers/tasks-fetchers";
import { logger } from "@/src/lib/logging/logger";
import { Referral, PaginatedReferral, ReferralStatus } from "@/src/models/documents/referral";
import {
  getAllReferrals,
  getReferralById,
  getAllPatientReferrals,
  getLatestPatientReferral,
  getPaginatedReferrals,
  getReferralFacetCounts,
  updateReferralStatus,
  getReferralAppointments,
  getReferralUpcomingAppointments,
  updateReferralNote,
} from "@/src/requests/referral";
import { isNotFound } from "@/src/requests/utils/fetch-json";

export const REFERRALS_CACHE_KEY = "referrals";
export const PAGINATED_REFERRALS_CACHE_KEY = "paginated-referrals";
export const REFERRAL_FACET_COUNTS_CACHE_KEY = "referral-facet-counts";

export function useReferrals() {
  const query = useQuery({
    queryKey: [REFERRALS_CACHE_KEY],
    queryFn: getAllReferrals,
    retry: false,
    staleTime: Infinity,
    refetchInterval: 60 * 1000,
  });

  return {
    referralsMap: query.data || {},
    referrals: Object.values(query.data || {}).sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }),
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useReferralById(displayId: string, options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();

  const {
    data: referral,
    isFetching,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [REFERRALS_CACHE_KEY, displayId],
    queryFn: async () => {
      // First check if the referral is in our global referrals cache
      const referralsCache = queryClient.getQueryData<Record<string, Referral>>([REFERRALS_CACHE_KEY]);

      if (referralsCache && referralsCache[displayId]) {
        logger.info("Found referral in cache");

        return referralsCache[displayId];
      }

      try {
        // If not in cache, fetch from server
        const referral = await getReferralById(displayId);

        if (referral) {
          // Update both the global cache and this specific query's cache
          updateReferralsQueryData(queryClient, referral);

          queryClient.setQueryData([REFERRALS_CACHE_KEY, displayId], referral);
        }

        return referral;
      } catch (err) {
        if (isNotFound(err as Error)) {
          return null;
        }

        throw err;
      }
    },
    staleTime: Infinity,
    enabled: options?.enabled !== false && !!displayId,
  });

  return { referral, isFetching, isLoading, isError };
}

export function usePatientReferrals(patientId?: string) {
  const referralsQuery = useQuery({
    queryKey: [REFERRALS_CACHE_KEY, "patient", patientId],
    queryFn: async () => {
      if (!patientId) {
        return [];
      }

      return getAllPatientReferrals(patientId);
    },
    enabled: Boolean(patientId),
    staleTime: Infinity,
    refetchInterval: 60 * 1000,
  });

  return {
    patientReferrals: referralsQuery.data || [],
    isFetching: referralsQuery.isFetching,
    isLoading: referralsQuery.isLoading,
    isError: referralsQuery.isError,
    error: referralsQuery.error,
    refetch: referralsQuery.refetch,
  };
}

export function useLatestPatientReferral(patientId?: string) {
  const query = useQuery({
    queryKey: [REFERRALS_CACHE_KEY, "patient", patientId, "latest"],
    queryFn: async () => {
      if (!patientId) {
        return null;
      }

      return getLatestPatientReferral(patientId);
    },
    enabled: Boolean(patientId),
    staleTime: Infinity,
    refetchInterval: 60 * 1000,
  });

  return {
    latestReferral: query.data || null,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useReferralUpdateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (referral: Referral) => {
      const result = await createOrUpdateReferral(referral, referral.patient, true);

      if (!result) {
        throw new Error("Failed to update referral");
      }

      return result;
    },
    onMutate: async (referral) => {
      await queryClient.cancelQueries({ queryKey: [REFERRALS_CACHE_KEY] });

      const previousReferrals = queryClient.getQueryData<Record<string, Referral>>([REFERRALS_CACHE_KEY]) ?? {};

      const referralsCache = queryClient.getQueryData<Record<string, Referral>>([REFERRALS_CACHE_KEY]) ?? {};

      if (referralsCache[referral.id]) {
        updateReferralsQueryData(queryClient, referral);
      }

      return { previousReferrals };
    },
    onError: (error, variables, context) => {
      logger.error("Error updating referral", {
        referralId: variables.id,
        error,
      });

      if (context) {
        queryClient.setQueryData([REFERRALS_CACHE_KEY], context.previousReferrals);

        if (variables.displayId && context.previousReferrals[variables.displayId]) {
          queryClient.setQueryData(
            [REFERRALS_CACHE_KEY, variables.displayId],
            context.previousReferrals[variables.displayId]
          );
        }
      }

      toast({
        title: "Something went wrong updating the referral. Please refresh the page and try again.",
        description: error.message,
        variant: "destructive",
      });
    },
    meta: {
      invalidates: [[TASKS_CACHE_KEY], [TASK_CACHE_KEY]],
    },
  });
}

export const updateReferralsQueryData = (queryClient: QueryClient, updatedReferral: Referral) => {
  queryClient.setQueryData([REFERRALS_CACHE_KEY], (oldData: Record<string, Referral> = {}) => {
    return {
      ...oldData,
      [updatedReferral.id]: updatedReferral,
    };
  });

  queryClient.setQueryData([REFERRALS_CACHE_KEY, updatedReferral.displayId], updatedReferral);
};

export interface PaginatedReferralsResponse {
  referrals: PaginatedReferral[];
  availableDepartments: { id: string; label: string }[];
  totalCount: number;
  hasMore: boolean;
  page: number;
  pageSize: number;
}

export interface ReferralFilters {
  schedulingPriority?: string[];
  status?: string[];
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export function usePaginatedReferrals({
  filters,
  columnFilters,
  page = 0,
  pageSize = 30,
  sortBy,
  sortOrder,
  query,
}: {
  filters?: ReferralFilters;
  columnFilters?: {
    schedulingPriority?: string[];
    status?: string[];
    dateFrom?: string;
    dateTo?: string;
    referringProvider?: string[];
    receivingProvider?: string[];
    receivingLocation?: string[];
    department?: string[];
    diagnosisCode?: string[];
    engagementOutcome?: string[];
    syncedToEhr?: boolean;
  };
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  query?: string;
} = {}) {
  const queryClient = useQueryClient();

  const referralsQuery = useQuery({
    queryKey: [PAGINATED_REFERRALS_CACHE_KEY, filters, columnFilters, page, pageSize, sortBy, sortOrder, query],
    queryFn: () =>
      getPaginatedReferrals({
        page,
        pageSize,
        schedulingPriority: columnFilters?.schedulingPriority,
        status: columnFilters?.status,
        dateFrom: columnFilters?.dateFrom,
        dateTo: columnFilters?.dateTo,
        referringProvider: columnFilters?.referringProvider,
        receivingProvider: columnFilters?.receivingProvider,
        receivingLocation: columnFilters?.receivingLocation,
        department: columnFilters?.department,
        diagnosisCode: columnFilters?.diagnosisCode,
        engagementOutcome: columnFilters?.engagementOutcome,
        syncedToEhr: columnFilters?.syncedToEhr === true ? true : undefined,
        sortBy,
        sortOrder,
        query,
      }),
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const paginatedData: PaginatedReferralsResponse | undefined = referralsQuery.data;
  const referrals: PaginatedReferral[] = paginatedData?.referrals || [];

  const hasMore = (!referralsQuery?.isFetching && paginatedData?.hasMore) ?? false;

  if (hasMore) {
    queryClient.prefetchQuery({
      queryKey: [PAGINATED_REFERRALS_CACHE_KEY, filters, columnFilters, page + 1, pageSize, sortBy, sortOrder, query],
      queryFn: () =>
        getPaginatedReferrals({
          page: page + 1,
          pageSize,
          schedulingPriority: columnFilters?.schedulingPriority,
          status: columnFilters?.status,
          dateFrom: columnFilters?.dateFrom,
          dateTo: columnFilters?.dateTo,
          referringProvider: columnFilters?.referringProvider,
          receivingProvider: columnFilters?.receivingProvider,
          receivingLocation: columnFilters?.receivingLocation,
          department: columnFilters?.department,
          diagnosisCode: columnFilters?.diagnosisCode,
          engagementOutcome: columnFilters?.engagementOutcome,
          syncedToEhr: columnFilters?.syncedToEhr === true ? true : undefined,
          sortBy,
          sortOrder: sortOrder ?? "desc",
          query,
        }),
      staleTime: 30 * 1000,
    });
  }

  const referralsMap: Record<string, PaginatedReferral> = {};

  referrals.forEach((referral: PaginatedReferral) => {
    referralsMap[referral.id] = referral;
  });

  return {
    referralsMap,
    referrals,
    availableDepartments: paginatedData?.availableDepartments || [],
    totalCount: paginatedData?.totalCount || 0,
    hasMore: paginatedData?.hasMore || false,
    page: paginatedData?.page || page,
    pageSize: paginatedData?.pageSize || pageSize,
    isFetching: referralsQuery.isFetching,
    isLoading: referralsQuery.isLoading,
    isError: referralsQuery.isError,
    refetch: referralsQuery.refetch,
  };
}

export interface ReferralFacetCountsFilters {
  schedulingPriority?: string[];
  status?: string[];
  referringProvider?: string[];
  department?: string[];
  query?: string;
}

// Fetches counts for referrals DataTable filters
export function useReferralFacetCountsQuery(filters: ReferralFacetCountsFilters = {}) {
  return useQuery({
    queryKey: [REFERRAL_FACET_COUNTS_CACHE_KEY, filters],
    queryFn: () => getReferralFacetCounts(filters),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
}

export function useReferralStatusMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      referralId,
      status,
      closeOutNote,
      appointmentId,
      skipReferralUpdateFax,
    }: {
      referralId: string;
      status: ReferralStatus;
      closeOutNote: string | null;
      appointmentId: string | null;
      skipReferralUpdateFax?: boolean;
    }) => {
      return updateReferralStatus(referralId, status, closeOutNote, appointmentId, skipReferralUpdateFax);
    },
    onMutate: async ({ referralId, status }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [REFERRALS_CACHE_KEY] });

      await queryClient.cancelQueries({ queryKey: [PAGINATED_REFERRALS_CACHE_KEY] });

      // Snapshot the previous value
      const previousReferrals = queryClient.getQueryData<Record<string, Referral>>([REFERRALS_CACHE_KEY]);

      const previousPaginatedData = queryClient.getQueryData<PaginatedReferralsResponse>([
        PAGINATED_REFERRALS_CACHE_KEY,
      ]);

      // Optimistically update the cache
      if (previousReferrals) {
        const referral = previousReferrals[referralId];

        if (referral) {
          updateReferralsQueryData(queryClient, { ...referral, status });
        }
      }

      return { previousReferrals, previousPaginatedData };
    },
    onError: (error, variables, context) => {
      logger.error("Error updating referral status", {
        referralId: variables.referralId,
        error,
      });

      // Rollback on error
      if (context?.previousReferrals) {
        queryClient.setQueryData([REFERRALS_CACHE_KEY], context.previousReferrals);
      }

      if (context?.previousPaginatedData) {
        queryClient.setQueryData([PAGINATED_REFERRALS_CACHE_KEY], context.previousPaginatedData);
      }

      toast({
        title: "Something went wrong updating the referral status. Please refresh the page and try again.",
        description: error.message,
        variant: "destructive",
      });
    },
    onSuccess: (data) => {
      // Update the cache with the server response
      updateReferralsQueryData(queryClient, data);

      // Invalidate paginated queries to refetch with updated data
      queryClient.invalidateQueries({ queryKey: [PAGINATED_REFERRALS_CACHE_KEY] });
    },
  });
}

export function useUpdateReferralNoteMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ referralId, note }: { referralId: string; note: string | null }) => {
      return updateReferralNote(referralId, note);
    },
    onMutate: async ({ referralId, note }) => {
      await queryClient.cancelQueries({ queryKey: [PAGINATED_REFERRALS_CACHE_KEY] });
      await queryClient.cancelQueries({ queryKey: [REFERRALS_CACHE_KEY] });

      const previousPaginatedData = queryClient.getQueriesData<PaginatedReferralsResponse>({
        queryKey: [PAGINATED_REFERRALS_CACHE_KEY],
      });
      const previousReferralsMap = queryClient.getQueryData<Record<string, Referral>>([REFERRALS_CACHE_KEY]);
      const previousReferralEntries = queryClient.getQueriesData<Referral>({ queryKey: [REFERRALS_CACHE_KEY] });

      queryClient.setQueriesData<PaginatedReferralsResponse>({ queryKey: [PAGINATED_REFERRALS_CACHE_KEY] }, (old) => {
        if (!old) return old;

        return {
          ...old,
          referrals: old.referrals.map((r) => (r.id === referralId ? { ...r, note } : r)),
        };
      });

      // Update the global referrals map used by the referral details toolbar.
      queryClient.setQueryData<Record<string, Referral>>([REFERRALS_CACHE_KEY], (old) => {
        if (!old) return old;
        const existing = old[referralId];

        if (!existing) return old;

        const updated = { ...existing, note };
        const next = { ...old, [referralId]: updated };

        queryClient.setQueryData([REFERRALS_CACHE_KEY, updated.displayId], updated);

        return next;
      });

      return { previousPaginatedData, previousReferralsMap, previousReferralEntries };
    },
    onError: (error, _variables, context) => {
      logger.error("Error updating referral note", { error });

      if (context?.previousPaginatedData) {
        for (const [queryKey, data] of context.previousPaginatedData) {
          queryClient.setQueryData(queryKey, data);
        }
      }

      if (context?.previousReferralsMap) {
        queryClient.setQueryData([REFERRALS_CACHE_KEY], context.previousReferralsMap);
      }

      if (context?.previousReferralEntries) {
        for (const [queryKey, data] of context.previousReferralEntries) {
          queryClient.setQueryData(queryKey, data);
        }
      }

      toast({
        title: "Something went wrong updating the note. Please try again.",
        description: error.message,
        variant: "destructive",
      });
    },
    onSuccess: (updated) => {
      if (updated) {
        updateReferralsQueryData(queryClient, updated);
      }

      queryClient.invalidateQueries({ queryKey: [PAGINATED_REFERRALS_CACHE_KEY] });
    },
  });
}

export function useReferralAppointments(referralId: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [REFERRALS_CACHE_KEY, referralId, "appointments"],
    queryFn: async () => {
      const appointments = await getReferralAppointments(referralId);

      updateAppointmentsCache(queryClient, appointments);

      return appointments;
    },
    enabled: Boolean(referralId),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });

  return {
    appointments: query.data || [],
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

// Live-fetches the patient's upcoming appointments for the appointment-booked confirm dialog. Only
// runs while the dialog is open (options.enabled); no polling or focus refetch so the list can't
// shift under the user mid-review.
export function useReferralUpcomingAppointments(referralId: string, options?: { enabled?: boolean }) {
  const query = useQuery({
    queryKey: [REFERRALS_CACHE_KEY, referralId, "upcoming-appointments"],
    queryFn: () => getReferralUpcomingAppointments(referralId),
    enabled: options?.enabled !== false && Boolean(referralId),
    staleTime: 30 * 1000,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    appointments: query.data ?? [],
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
